import { Link } from 'react-router-dom';
import VerdictBadge from '@/components/base/VerdictBadge';
import { credibilityBarClass, credibilityLabel } from '@/lib/verdict';
import { formatHeat } from '@/lib/content';
import type { ExposeItem } from '@/types/content';

interface ExposeCardProps {
  item: ExposeItem;
  layout?: 'grid' | 'row';
  rank?: number;
}

export default function ExposeCard({ item, layout = 'grid', rank }: ExposeCardProps) {
  if (layout === 'row') {
    return (
      <article className="group flex gap-4 md:gap-5 py-5 border-b border-background-200 last:border-b-0">
        {typeof rank === 'number' && (
          <div className="hidden sm:flex w-8 shrink-0 items-start pt-1">
            <span className="font-heading text-2xl font-bold text-background-400">
              {String(rank).padStart(2, '0')}
            </span>
          </div>
        )}
        <div className="w-28 h-20 md:w-44 md:h-28 shrink-0 rounded-lg overflow-hidden bg-background-100">
          <img
            src={item.cover}
            alt={`${item.title} 相关核实配图`}
            title={`${item.title} - 看料爆料核实`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-foreground-500 border border-background-300 rounded-md px-2 py-0.5 whitespace-nowrap">
              {item.category}
            </span>
            <VerdictBadge verdict={item.verdict} />
          </div>
          <h3 className="mt-2 font-heading text-base md:text-lg font-semibold text-foreground-950 leading-snug line-clamp-2">
            <Link to={`/expose/${item.id}`} className="hover:text-primary-600 transition-colors">
              {item.title}
            </Link>
          </h3>
          <p className="mt-1.5 text-sm text-foreground-600 line-clamp-2 hidden sm:block">{item.summary}</p>
          <div className="mt-2.5 flex items-center gap-4 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-fire-line text-primary-500"></i>
              </span>
              热度 {formatHeat(item.heat)}
            </span>
            <span>{item.publishedAt}</span>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex flex-col overflow-hidden rounded-xl border border-background-200 bg-background-50 hover:border-primary-300 transition-colors">
      <Link to={`/expose/${item.id}`} className="block">
        <div className="w-full aspect-[4/3] overflow-hidden bg-background-100">
          <img
            src={item.cover}
            alt={`${item.title} 核实配图`}
            title={`${item.title} - 看料爆料核实`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        </div>
      </Link>
      <div className="flex flex-1 flex-col p-4 md:p-5">
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-foreground-500 border border-background-300 rounded-md px-2 py-0.5 whitespace-nowrap">
            {item.category}
          </span>
          <VerdictBadge verdict={item.verdict} />
        </div>
        <h3 className="mt-3 font-heading text-base md:text-lg font-semibold text-foreground-950 leading-snug line-clamp-2">
          <Link to={`/expose/${item.id}`} className="hover:text-primary-600 transition-colors">
            {item.title}
          </Link>
        </h3>
        <p className="mt-2 text-sm text-foreground-600 line-clamp-2 flex-1">{item.summary}</p>

        <div className="mt-4">
          <div className="flex items-center justify-between text-xs text-foreground-500 mb-1.5">
            <span>可信度 {credibilityLabel(item.credibility)}</span>
            <span className="font-semibold text-foreground-800">{item.credibility}</span>
          </div>
          <div className="h-1.5 w-full rounded-full bg-background-200 overflow-hidden">
            <div
              className={`h-full rounded-full ${credibilityBarClass(item.credibility)}`}
              style={{ width: `${item.credibility}%` }}
            ></div>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-3 text-xs text-foreground-500">
          <span className="flex items-center gap-1">
            <span className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-fire-line text-primary-500"></i>
            </span>
            热度 {formatHeat(item.heat)}
          </span>
          <span>{item.updatedAt} 更新</span>
        </div>
      </div>
    </article>
  );
}